import React, { useState, useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { DotLoading, Toast } from 'antd-mobile';

interface SharedRouteProps {
  children: React.ReactNode;
}

const SharedRoute: React.FC<SharedRouteProps> = ({ children }) => {
  const { shareId } = useParams<{ shareId: string }>();
  const { isAuthenticated, login, logout } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isValidShare, setIsValidShare] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);
  
  useEffect(() => {
    // 如果已经验证过，且验证成功，不再重复验证
    if (hasValidated && isValidShare) {
      setLoading(false);
      return;
    }
    
    const validateShare = async () => {
      try {
        if (!shareId) {
          setIsValidShare(false);
          setLoading(false);
          // 如果没有分享ID，清除用户登录状态
          logout();
          return;
        }
        
        const result = await api.validateShareLink(shareId);
        
        if (result.success && result.data?.token) {
          // 分享链接有效，使用返回的token和用户信息
          const token = result.data.token;
          const user = result.data.user;
          
          // 在用户信息中添加shareId和isSharedAccess标记
          const updatedUser = {
            ...user,
            shareId: shareId,
            isSharedAccess: true
          };
          
          // 保存shareId到localStorage，以便其他组件可以访问
          localStorage.setItem('current_share_id', shareId);
          
          // 保存登录状态和用户信息
          login(token, updatedUser);
          
          setIsValidShare(true);
          // 标记已经验证过
          setHasValidated(true);
        } else {
          // 分享链接无效，完全退出登录
          Toast.show({
            icon: 'fail',
            content: result.error || '分享链接已过期或无效',
            position: 'center',
            duration: 3000,
          });
          setIsValidShare(false);
          
          // 调用logout方法清除所有登录状态和分享状态
          logout();
        }
      } catch (error) {
        console.error('验证分享链接错误:', error);
        Toast.show({
          icon: 'fail',
          content: '网络错误，请稍后再试',
          position: 'center',
        });
        setIsValidShare(false);
        // 出错时也清除登录状态
        logout();
      } finally {
        setLoading(false);
      }
    };
    
    validateShare();
  }, [shareId, login, logout, hasValidated, isValidShare]);
  
  useEffect(() => {
    // 监听用户是否通过其他方式登录
    if (isAuthenticated) {
      console.log('用户已登录，标记分享验证成功');
      setIsValidShare(true);
      setLoading(false);
    }
  }, [isAuthenticated]);
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '10px'
      }}>
        <DotLoading color='primary' />
        <span>验证分享链接中...</span>
      </div>
    );
  }
  
  if (!isValidShare) {
    // 如果分享链接无效，重定向到登录页面
    return <Navigate to="/login" replace />;
  }
  
  // 跳转到首页
  return <Navigate to="/" replace />;
};

export default SharedRoute; 